import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';

/**
 * PrivacyPage - Privacy Policy page
 * Migrated from public/privacy.html with all original content preserved
 */
export function PrivacyPage() {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      <Navbar />

      {/* Header */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-xl text-primary-200">Last updated: January {currentYear}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Privacy-First Highlight */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <div className="flex items-start space-x-3">
                <div className="text-green-600 text-2xl">🔒</div>
                <div>
                  <h3 className="text-green-800 font-bold text-lg mb-2">Privacy-First Design</h3>
                  <p className="text-green-700 font-semibold">
                    FinModel is designed with privacy in mind. Your financial data stays in your browser and
                    is never transmitted to our servers.
                  </p>
                </div>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Don't Collect</h2>
              <p className="mb-4 text-slate-600">
                FinModel is designed to protect your privacy. We do NOT collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-600">
                <li>Your financial data or business assumptions</li>
                <li>Generated financial models or projections</li>
                <li>Personal financial information</li>
                <li>Business plans or proprietary data</li>
                <li>Excel files or exported data</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How FinModel Works</h2>
              <p className="mb-6 text-slate-600">
                All financial calculations and data processing happen locally in your web browser. When you
                input assumptions and generate models, this data is processed entirely on your device and is
                never sent to our servers.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Information We May Collect</h2>
              <p className="mb-4 text-slate-600">We may collect limited, non-financial information for technical purposes:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-600">
                <li><strong>Usage Analytics:</strong> Basic website usage statistics (page views, user agent)</li>
                <li><strong>Technical Data:</strong> Browser type, device type for compatibility</li>
                <li><strong>Error Reports:</strong> Technical errors to improve the service (no financial data)</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Cookies and Local Storage</h2>
              <p className="mb-6 text-slate-600">
                We may use local browser storage to save your preferences and improve user experience. This data
                remains on your device and is not transmitted to our servers. You can clear this data at any
                time through your browser settings.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Third-Party Services</h2>
              <p className="mb-6 text-slate-600">
                FinModel may use third-party services for hosting, analytics, and content delivery. These
                services operate under their own privacy policies. We ensure that no financial data is shared
                with these services.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Security</h2>
              <p className="mb-6 text-slate-600">
                Since your financial data never leaves your browser, it remains under your control. We recommend
                using up-to-date browsers and following standard security practices when using any financial
                tools.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Your Rights</h2>
              <p className="mb-4 text-slate-600">You have the right to:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-600">
                <li>Use FinModel without providing personal information</li>
                <li>Clear local browser data at any time</li>
                <li>Request information about any data we collect</li>
                <li>Opt out of analytics or tracking</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Changes to Privacy Policy</h2>
              <p className="mb-6 text-slate-600">
                We may update this Privacy Policy from time to time. We will notify users of any significant
                changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Contact Information</h2>
              <p className="mb-6 text-slate-600">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:{' '}
                <a href="mailto:hello@finmodel.app" className="text-primary-600 hover:underline">
                  hello@finmodel.app
                </a>
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. International Users</h2>
              <p className="mb-6 text-slate-600">
                FinModel is designed to comply with major privacy regulations including GDPR, CCPA, and similar
                frameworks. Since we don't collect personal financial data, most privacy concerns are naturally
                addressed by our architecture.
              </p>
            </div>

            {/* Footer Cards */}
            <div className="mt-8 pt-8 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                  <h3 className="text-primary-800 font-semibold mb-2">🔐 Your Data Stays Local</h3>
                  <p className="text-primary-700 text-sm">
                    All your financial models and calculations happen in your browser.
                    We never see or store your business data.
                  </p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-green-800 font-semibold mb-2">📊 Anonymous Usage Only</h3>
                  <p className="text-green-700 text-sm">
                    We only collect anonymous usage statistics to improve the platform.
                    No personal or financial information is tracked.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
